import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import { isNil, omit } from 'lodash'
import {
  faArrowLeft,
  faTrash,
  faCopy,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons'
import {
  faCalendarUsers,
  faCalendar,
  faHourglassHalf,
} from '@fortawesome/pro-solid-svg-icons'
import {
  Accordion,
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  CardColors,
  CardVariants,
  Form,
  HeroCard,
  Icon,
  Image,
  Separator,
  Text,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import { getFieldNames, makeNumberSchema, makeReqStringSchema } from '@/base'
import {
  AddUserIncomePlanSourceToForm,
  BULK_UPDATE_USER_INCOME_MONTHS_KEY,
  bulkUpdateUserIncomeMonths,
  CREATE_USER_INCOME_PLAN_KEY,
  createUserIncomePlan,
  fetchUserIncomePlans,
  selectUserIncomePlanById,
  UPDATE_USER_INCOME_PLAN_KEY,
  updateUserIncomePlan,
} from '@/features/income-planning/incomePlanning.slice'
import {
  getNameForCopy,
  useSelectedMonthOptions,
} from '@/features/income-planning/utils'
import { AddIncomeSourceDialog } from '@/features/income-planning/plan-flows/AddIncomeSourceDialog'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { DeletePlanDialog } from '@/features/income-planning/plan-flows/DeletePlanDialog'
import { PlanInfoCard } from '@/features/income-planning/PlanInfoCard'
import { centsToDollars, dollarsToCents } from '@/utils/currencyHelpers'
import { selectFirstErrorMessageForKeys } from '@/reducers/fetch'
import { PayerTypeSelect } from '@/features/income-planning/plan-flows/PayerTypeSelect'
import { useLocalStorageBool } from '@/utils/localStorage'

const INCOME_PLANNING_CREATE_INTRO_DISMISSED_KEY =
  'INCOME_PLANNING_CREATE_INTRO_DISMISSED_KEY'

//todo icons badges in here have the wrong design
// todo borders on focused inputs not working
export const CreateEditIncomePlan = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  // An id will be in url if editing
  const { id } = useParams()
  const planId = Number(id)
  const currentPlan = useReselector(selectUserIncomePlanById, planId)

  // An id will be in search params if creating a plan from existing plan
  const [searchParams] = useSearchParams()
  const fromPlanId = searchParams.get('fromPlan')
    ? Number(searchParams.get('fromPlan'))
    : null
  const fromPlan = useReselector(selectUserIncomePlanById, fromPlanId)

  const [modalOpen, setModalOpen] = useState(false)

  const selectedMonthOptions = useSelectedMonthOptions()

  const { isTrue: introDismissed, setTrue: setIntroDismissed } =
    useLocalStorageBool(INCOME_PLANNING_CREATE_INTRO_DISMISSED_KEY)

  const error = useReselector(selectFirstErrorMessageForKeys, [
    CREATE_USER_INCOME_PLAN_KEY,
    UPDATE_USER_INCOME_PLAN_KEY,
    BULK_UPDATE_USER_INCOME_MONTHS_KEY,
  ])

  useEffect(() => {
    dispatch(fetchUserIncomePlans())
  }, [dispatch])

  const formik = useFormik({
    initialValues: {
      planName: currentPlan?.planName || '', // Plan name not copied if from existing
      // Form inputs use dollar inputs so mapping needed
      incomePlanSources: (
        currentPlan?.incomePlanSources ||
        fromPlan?.incomePlanSources || [
          {
            name: 'Client Session',
            source: 'therapy',
            cadence: 'hourly',
            rateInCents: 15000,
            numberPerMonth: 20,
            payerType: undefined,
          },
        ]
      ).map((p) => ({
        ...p,
        rate: centsToDollars(p.rateInCents),
      })),
      selectedMonths: [] as string[], // This isn't saved on the BE so will be initialized as empty array even when editing
    },
    enableReinitialize: true,
    onSubmit: async ({ planName, selectedMonths, incomePlanSources }) => {
      // Convert dollar inputs back to cents
      const updatedIncomeSources = incomePlanSources.map(
        ({ rate, numberPerMonth, ...rest }) => ({
          ...rest,
          rateInCents: dollarsToCents(rate),
          numberPerMonth: isNil(numberPerMonth) ? null : Number(numberPerMonth),
        })
      )

      const plan = currentPlan
        ? await dispatch(
            updateUserIncomePlan(currentPlan.id, {
              planName,
              planSources: updatedIncomeSources,
            })
          )
        : await dispatch(
            createUserIncomePlan({
              planName,
              planSources: updatedIncomeSources,
            })
          )

      if (!plan) {
        return
      }

      let updatedPlansIds: number[] = []

      if (selectedMonths.length > 0) {
        const bulkUpdateMonthResponse = await dispatch(
          bulkUpdateUserIncomeMonths({
            planId: plan.id,
            months: selectedMonths.map((month) => ({
              month: Number(month.split('-')[1]),
              year: Number(month.split('-')[0]),
            })),
          })
        )

        if (!bulkUpdateMonthResponse) {
          return
        }

        updatedPlansIds = bulkUpdateMonthResponse.map((m) => m.id)
      }

      navigate('/income-planning', {
        state: {
          updatedPlansIds,
          planCreated: !currentPlan,
          planUpdated: Boolean(currentPlan),
        },
      })
    },
  })

  const { values, setFieldValue, submitForm } = formik
  const fieldNames = getFieldNames(formik)

  return (
    <FormikProvider value={formik}>
      <Breadcrumb>
        <Breadcrumb.List>
          <Breadcrumb.Item>
            <Breadcrumb.Link onClick={() => navigate('/income-planning')}>
              <Icon icon={faArrowLeft} />
              Back to Income Planning
            </Breadcrumb.Link>
          </Breadcrumb.Item>
        </Breadcrumb.List>
      </Breadcrumb>

      {!introDismissed && (
        <HeroCard
          variant={CardVariants.LEFT_ALIGNED}
          imageAlt="income planning"
          imageSrc="https://heard-images.s3.amazonaws.com/assets/income_planning.svg"
          title="Creating a plan"
          subheader="income planning"
          description="Easily plan out different scenarios personalized for your practice."
          onClose={setIntroDismissed}
          color={CardColors.EMPHASIS}
        >
          <div className="flex items-start gap-4 mt-4">
            <Badge color="olive" size="md" variant="secondary">
              <Text variant={TypographyStyles.BODY_LG}>1</Text>
            </Badge>
            <div>
              <Text variant={TypographyStyles.LABEL_MD}>
                Add practice income streams
              </Text>
              <Text variant={TypographyStyles.BODY_SM} className="mt-2">
                Account for both therapy and non-therapy income and adjust the
                amounts.
              </Text>
            </div>
          </div>
          <div className="flex items-start gap-4 mt-4">
            <Badge color="olive" size="md" variant="secondary">
              <Text variant={TypographyStyles.BODY_LG}>2</Text>
            </Badge>
            <div>
              <Text variant={TypographyStyles.LABEL_MD}>
                Apply your plan to specific months
              </Text>
              <Text variant={TypographyStyles.BODY_SM} className="mt-2">
                Choose the applicable months, or skip this step to plan for
                specific scenarios that your practice encounters whether that’s
                busy months, summer months or holidays.
              </Text>
            </div>
          </div>
          <div className="flex items-start gap-4 mt-4">
            <Badge color="olive" size="md" variant="secondary">
              <Text variant={TypographyStyles.BODY_LG}>3</Text>
            </Badge>
            <div>
              <Text variant={TypographyStyles.LABEL_MD}>
                Compare and reevaluate your plans
              </Text>
              <Text variant={TypographyStyles.BODY_SM} className="mt-2">
                When you return to update your plan, we’ll highlight any changes
                in your income based upon your edits.
              </Text>
            </div>
          </div>
          {/*todo ZEN-2164 there's a walkthrough link here*/}
        </HeroCard>
      )}

      {currentPlan ? (
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/pencil.svg"
          alt="pencil"
          width={80}
          className="mt-10"
        />
      ) : (
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/ledger.svg"
          alt="ledger"
          width={80}
          className="mt-10"
        />
      )}
      <Text as={TypographyStyles.DISPLAY_MD} className="mt-4">
        {currentPlan ? 'Edit plan' : 'Create new plan'}
      </Text>
      <Text as={TypographyStyles.BODY_SM} className="mt-4">
        {currentPlan
          ? 'Update your plan and decide which months to apply it to.'
          : 'Plan your income flexibly by accounting for different income streams, rates, and number of sessions to see how it affects your income. Choose which months this template should apply to.'}
      </Text>

      {error && <Alert variant="error" title={error} className="mt-4" />}
      <div className="flex gap-10">
        <div className="w-5/8">
          <Card>
            <Form.Input
              name={fieldNames.planName}
              label="Plan Name"
              schema={makeReqStringSchema()}
            />
            <div className="mt-4">
              <Form.Multiselect
                label="Apply to Months (Optional):"
                options={selectedMonthOptions}
                name={fieldNames.selectedMonths}
                placeholder="Select months"
              />
            </div>
          </Card>

          {values.incomePlanSources.map((source, index) => (
            <Card key={index} className="mt-6">
              <div className="flex justify-between">
                <div className="flex">
                  <Icon
                    icon={
                      source.source === 'therapy'
                        ? faCalendarUsers
                        : source.cadence === 'monthly'
                          ? faCalendar
                          : faHourglassHalf
                    }
                    backgroundStyle="rounded"
                    backgroundColor="black"
                    size="sm"
                    color="text-neutral-0"
                    backgroundClassName="mr-2"
                  />
                  <div>
                    <Text variant={TypographyStyles.LABEL_MD}>
                      {source.name}
                    </Text>
                    <Text variant={TypographyStyles.BODY_SM}>
                      {source.source === 'therapy'
                        ? 'Therapy Income'
                        : 'Non-Therapy Income'}
                    </Text>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const newList = [...values.incomePlanSources]
                      newList.splice(index, 1)
                      setFieldValue(fieldNames.incomePlanSources, newList)
                    }}
                    className="mr-2"
                  >
                    <Icon icon={faTrash} />
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setFieldValue(fieldNames.incomePlanSources, [
                        ...values.incomePlanSources,
                        {
                          ...omit(
                            source,
                            'id',
                            'incomePlanId',
                            'updatedAt',
                            'createdAt'
                          ),
                          name: getNameForCopy(
                            source.name,
                            values.incomePlanSources
                          ),
                        },
                      ])
                    }}
                  >
                    <Icon icon={faCopy} />
                  </Button>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <div className="w-1/2">
                  <Form.Input
                    name={`incomePlanSources[${index}].rate`}
                    label={
                      source.source === 'therapy'
                        ? 'Session rate'
                        : source.cadence === 'hourly'
                          ? 'Hourly rate'
                          : 'Amount'
                    }
                    schema={makeNumberSchema({
                      allowedDecimals: 2,
                      minNumber: 0,
                    })}
                    componentType="currency"
                  />
                  {source.cadence === 'hourly' && (
                    <Form.Slider
                      name={`incomePlanSources[${index}].rate`}
                      min={0}
                      step={5}
                      max={400}
                      wrapperClassName="mt-4"
                    />
                  )}
                </div>
                <div className="w-1/2">
                  {source.cadence === 'monthly' ? (
                    <Form.Select
                      label="Period"
                      name={`incomePlanSources[${index}].cadence`}
                      disabled
                      options={[{ value: 'monthly', text: 'Per month' }]}
                    />
                  ) : (
                    <>
                      <Form.Input
                        name={`incomePlanSources[${index}].numberPerMonth`}
                        label={
                          source.source === 'therapy'
                            ? '# per month'
                            : 'Hours per month'
                        }
                        schema={makeNumberSchema({
                          allowedDecimals: 2,
                          minNumber: 0,
                        })}
                      />
                      <Form.Slider
                        name={`incomePlanSources[${index}].numberPerMonth`}
                        min={0}
                        step={5}
                        max={400}
                        wrapperClassName="mt-4"
                      />
                    </>
                  )}
                </div>
              </div>
              {source.source === 'therapy' && (
                <div className="mt-4">
                  <Accordion
                    variant="text"
                    type="single"
                    collapsible
                    defaultValue={
                      values.incomePlanSources?.[index]?.payerType
                        ? `payerType-${index}`
                        : undefined
                    }
                  >
                    <Accordion.Item value={`payerType-${index}`}>
                      <Accordion.Trigger>
                        <Text variant={TypographyStyles.H3}>
                          Payer type (Optional)
                        </Text>
                      </Accordion.Trigger>
                      <Accordion.Content>
                        <PayerTypeSelect
                          name={`incomePlanSources[${index}].payerType`}
                          placeholder="Payer types not included in the selections above"
                        />
                      </Accordion.Content>
                    </Accordion.Item>
                  </Accordion>
                </div>
              )}
            </Card>
          ))}

          {values.incomePlanSources.length === 0 && (
            <Card className="mt-6" color={CardColors.LEVEL_1}>
              <Text variant={TypographyStyles.BODY_SM} textAlign="center">
                Use the <b>Add Income</b> button to begin
              </Text>
            </Card>
          )}

          <Button
            className="w-full mt-6"
            onClick={() => setModalOpen(true)}
            variant="secondary"
          >
            Add Income
          </Button>
        </div>

        <PlanInfoCard
          className="w-3/8"
          initialPlanToCompare={fromPlanId || planId} // If creating or editing a plan from existing plan default to existing plan
          showComparePlan
          sources={values.incomePlanSources}
        />
      </div>
      <Separator className="mt-10" />
      <div className="w-full flex justify-between mt-6">
        <Button
          onClick={() => navigate('/income-planning')}
          variant="secondary"
          iconLeft={faChevronLeft}
        >
          Back
        </Button>
        <div className="flex gap-2">
          {currentPlan && <DeletePlanDialog planId={currentPlan.id} />}
          <Button onClick={submitForm} iconRight={faChevronRight}>
            Save
          </Button>
        </div>
      </div>
      {modalOpen && (
        <AddIncomeSourceDialog
          onCancel={() => setModalOpen(false)}
          onSubmit={(source: AddUserIncomePlanSourceToForm) => {
            setFieldValue(fieldNames.incomePlanSources, [
              ...values.incomePlanSources,
              source,
            ])
            setModalOpen(false)
          }}
        />
      )}
    </FormikProvider>
  )
}
